'use client';
import React, { FC, useState } from 'react';
import { Button, GenInternalLink, GenNavTile, HeaderText } from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import { TypeAccountNavTileFields } from '@/core/generated-types';
import {
  TypeUserPreferenceRecord,
  TypeUserRecord,
} from '@/lib/api/accountManager/types';
import { TypeAccountSettingsPageTranslations } from '@/utils/translations/types/TypeAccountSettingsPageTranslations';
import { editUser } from '@/lib/api/accountManager/apis/editUser';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';
import { Alert } from '@/components/ui/Alert';
import PhoneNumberInput, {
  getCountryCodeFromLocale,
} from '../../EditCommunicationPreferences/PhoneNumberInput';

export type CommunicationPreferencesTileProps = {
  t: TypeAccountSettingsPageTranslations;
  user: TypeUserRecord;
  tile: TypeAccountNavTileFields;
  locale: string | undefined;
};

const _getParsedNumber = (phoneNumber: string, locale: string): string => {
  const parsedNumber = parsePhoneNumberFromString(phoneNumber);

  if (parsedNumber) {
    return locale === 'US'
      ? parsedNumber?.formatNational()
      : parsedNumber?.formatInternational();
  } else {
    return '';
  }
};

const CommunicationPreferencesTile: FC<CommunicationPreferencesTileProps> = ({
  t,
  user,
  tile,
  locale,
}) => {
  const { title, subheader, description, subfooter } = tile;
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string>(
    user.phone_number || '',
  );
  const [phoneNumberValidationError, setPhoneNumberValidationError] =
    useState<boolean>(false);
  const [isServerError, setIsServerError] = useState(false);
  const editCommunicationPreferencesUrl = `/account/settings/edit-communication-preferences`;
  const countryCode = getCountryCodeFromLocale(locale);
  const isUserPhoneNumberPresent = Boolean(user.phone_number?.trim());
  const parsedNumber = _getParsedNumber(phoneNumber, countryCode);

  const clearErrors = () => {
    if (isServerError) {
      setIsServerError(false);
    }
    if (phoneNumberValidationError) {
      setPhoneNumberValidationError(false);
    }
  };

  const handleSubmit = async () => {
    setIsProcessing(true);
    setIsSuccess(true);
    try {
      const response = await editUser({
        userUuid: user.uuid,
        userData: {
          phone_number: phoneNumber,
          user_preferences: [
            {
              preference_name: 'marketing_sms',
              preference_value: true,
            },
            {
              preference_name: 'transactional_sms',
              preference_value: true,
            },
          ] as TypeUserPreferenceRecord[],
        },
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setIsServerError(true);
      }
    } catch (error) {
      setIsServerError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <GenNavTile
      title={title}
      titleDecorator={
        isUserPhoneNumberPresent && (
          <GenInternalLink
            href={editCommunicationPreferencesUrl}
            cta={t.editCta}
          />
        )
      }
    >
      {isUserPhoneNumberPresent ? (
        <>
          <p className="mb-6">{description}</p>
          <div className="flex flex-col lg:flex-row space-x-1">
            <strong>{t.phoneNumberLabel}</strong>
            <span>{parsedNumber}</span>
          </div>
        </>
      ) : (
        <div className="bg-indigo-pastel p-6">
          <HeaderText
            size={HeaderSize.H4}
            classes="font-bold"
            text={t.smsNotificationsLabel}
            hoverEffect
          />
          <p className="text-base pb-6">{subheader}</p>
          {isSuccess ? (
            <Alert message={t.smsSuccessMessage} />
          ) : (
            <>
              <div className="pb-6">
                <PhoneNumberInput
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  placeholder={t.phonePlaceholderLabel}
                  locale={locale}
                  errorMessage={t.invalidPhoneNumberMessage}
                  onValidationError={() => {
                    setPhoneNumberValidationError(true);
                  }}
                  onClearError={clearErrors}
                />
              </div>
              <Button
                type="submit"
                ctaText={t.addMyNumberCta}
                onClick={handleSubmit}
                disabled={
                  phoneNumberValidationError || isProcessing || !phoneNumber
                }
                includeSpinner={isProcessing}
                fullWidth
              />
              {subfooter && (
                <p className="text-sm text-grey-light mt-4">{subfooter}</p>
              )}
            </>
          )}
        </div>
      )}
    </GenNavTile>
  );
};

export default React.memo(CommunicationPreferencesTile);
